import anime from 'animejs'
import debounce from 'lodash/debounce'
import Component from '../core/Component'
import Scroll from '../services/Scroll'
import { closest } from '../utils/dom'

export default class Button extends Component {
    constructor(element, options = {}) {
        super(element, options)

        this.variant = null
        if (window.IntersectionObserver) {
            this.observer = new IntersectionObserver(this.handleIntersection, {
                threshold: 1
            })
        }
        this.animTriggerCount = 0
    }

    prepare() {
        if (this.element.classList.contains('Button--square')) {
            this.variant = createVariantSquare(this.element)
        } else {
            this.variant = createVariantBasic(this.element)
        }

        const triggerEls = [
            this.element,
            ...closest(this.element, '.ButtonTrigger')
        ]
        triggerEls.forEach(triggerEl => {
            triggerEl.addEventListener('mouseenter', () => this.animateIn())
            triggerEl.addEventListener('mouseleave', () => this.animateOut())
        })
        this.element.addEventListener('focus', () => this.animateIn())
        this.element.addEventListener('blur',  () => this.animateOut())

        Scroll.on('resize', this.handleResize)
        this.observer.observe(this.element)
    }

    destroy() {
        Scroll.off('resize', this.handleResize)
    }

    handleIntersection = entries => {
        entries.forEach(entry => {
            if (entry.target !== this.element) {
                return
            }
            if (entry.isIntersecting) {
                this.variant.create()
                this.observer.disconnect()
                this.observer = null
            }
        })
    }

    animateIn() {
        this.animTriggerCount += 1

        if (this.animTriggerCount > 1) {
            return
        }

        const hoverAnimation = this.variant.getHoverAnimation()
        if (!hoverAnimation) {
            return
        }

        if (hoverAnimation.reversed) {
            hoverAnimation.reverse()
        }
        hoverAnimation.play()
    }

    animateOut() {
        if (this.animTriggerCount > 0) {
            this.animTriggerCount -= 1
        }

        if (this.animTriggerCount > 0) {
            return
        }

        const hoverAnimation = this.variant.getHoverAnimation()
        if (!hoverAnimation) {
            return
        }

        if (!hoverAnimation.reversed) {
            hoverAnimation.reverse()
        }
        hoverAnimation.play()
    }

    handleResize = () => {
        this.resize()
    }

    resize() {
        if (this.variant) {
            this.variant.destroy()
        }

        this.resizeEnd()
    }

    resizeEnd = debounce(() => {
        this.instantResize()
    }, 300)

    instantResize() {
        if (this.variant) {
            this.variant.create()
        }
    }
}



function createVariantSquare(element) {
    let hoverAnimation = null
    let isCreated = false

    function createBorderSvg() {
        const rect = element.getBoundingClientRect()
        const borderSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        borderSvg.classList.add('Button-borderSvg')
        borderSvg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`)
        borderSvg.innerHTML = `
            <path d="M0,0 L${rect.width},0" stroke="#3b3b3b" stroke-width="1" fill="none"/>
            <path d="M0,${rect.height} L${rect.width},${rect.height}" stroke="#3b3b3b" stroke-width="1" fill="none"/>
            <path data-ref="line1" d="M0,0 L${rect.width},0" style="opacity: 0" stroke="#F2BD5D" stroke-width="2" fill="none"/>
            <path data-ref="line2" d="M0,${rect.height} L${rect.width},${rect.height}" style="opacity: 0" stroke="#F2BD5D" stroke-width="2" fill="none"/>
        `
        element.appendChild(borderSvg)
    }


    function getHoverAnimation() {
        if (hoverAnimation) {
            return hoverAnimation
        }

        const line1 = element.querySelector('[data-ref="line1"]')
        const line2 = element.querySelector('[data-ref="line2"]')
        const line1Length = line1.getTotalLength()
        const line2Length = line2.getTotalLength()

        hoverAnimation = anime.timeline({
            easing: 'easeInOutQuad',
            duration: 400
        })

        hoverAnimation
            .add({
                targets: element,
                paddingLeft: [0,10],
                duration: 700
            })
            .add({
                targets: line1,
                opacity: [0, 1],
                strokeDashoffset: [line1Length, '0'],
                strokeDasharray: [line1Length, line1Length],
                duration: 600
            }, '-=500')
            .add({
                targets: line2,
                opacity: [0, 1],
                strokeDashoffset: [-line2Length, '0'],
                strokeDasharray: [line2Length, line2Length],
                duration: 400
            }, '-=600')
            .add({
                targets: element,
                color: ['#ffffff', '#F2BD5D'],
                duration: 600
            }, '-=800')
        return hoverAnimation
    }

    function create() {
        if (isCreated) {
            return
        }
        createBorderSvg()
        isCreated = true
    }

    function destroy() {
        const borderSvg = element.querySelector('.Button-borderSvg')
        if (borderSvg) {
            borderSvg.parentNode.removeChild(borderSvg)
        }
        hoverAnimation = null
        isCreated = false
    }

    return {
        create,
        destroy,
        getHoverAnimation
    }
}


function createVariantBasic(element) {
    let hoverAnimation = null
    let isCreated = false

    function getRoundedRectAsPath(width, height, strokeWidth = 1, radius = 0) {
        return `M${width / 2},${strokeWidth} L${width - radius},${strokeWidth} A${radius},${radius} 0 0,1 ${width - strokeWidth},${radius} L${width - strokeWidth},${height - radius} A${radius},${radius} 0 0,1 ${width - radius},${height - strokeWidth} L${radius},${height - strokeWidth} A${radius},${radius} 0 0,1 ${strokeWidth},${height - radius} L${strokeWidth},${radius} A${radius},${radius} 0 0,1 ${radius},${strokeWidth} Z`
    }

    function createBorderSvg() {
        const rect = element.getBoundingClientRect()
        const borderSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        borderSvg.classList.add('Button-borderSvg')
        borderSvg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`)
        borderSvg.innerHTML = `
            <path class="Button-borderOutline" d="${getRoundedRectAsPath(rect.width, rect.height, 1, 32)}" stroke="#3b3b3b" stroke-width="1" fill="none"/>
            <path data-ref="line1" d="${getRoundedRectAsPath(rect.width, rect.height, 1, 32)}" style="opacity: 0" stroke="#F2BD5D" stroke-width="1" fill="none"/>
            <path data-ref="line2" d="${getRoundedRectAsPath(rect.width, rect.height, 1, 32)}" style="opacity: 0" stroke="#F2BD5D" stroke-width="1" fill="none"/>
        `
        element.appendChild(borderSvg)
    }

    function getHoverAnimation() {
        if (hoverAnimation) {
            return hoverAnimation
        }

        const line1 = element.querySelector('[data-ref="line1"]')
        const line2 = element.querySelector('[data-ref="line2"]')
        const line1Length = line1.getTotalLength()
        const line2Length = line2.getTotalLength()

        hoverAnimation = anime.timeline({
            easing: 'easeInOutQuad',
            duration: 400
        })

        hoverAnimation
            .add({
                targets: line1,
                opacity: [0, 1],
                strokeDashoffset: [line1Length, 0],
                strokeDasharray: [line1Length, line1Length],
                duration: 1000
            })
            .add({
                targets: line2,
                opacity: [0, 1],
                strokeDashoffset: [-line2Length, 0],
                strokeDasharray: [line2Length, line2Length],
                duration: 700
            }, '-=700')
            .add({
                targets: element,
                color: ['#ffffff', '#F2BD5D'],
                duration: 600
            }, '-=1000')

        return hoverAnimation
    }

    function create() {
        if (isCreated) {
            return
        }
        createBorderSvg()
    }

    function destroy() {
        const borderSvg = element.querySelector('.Button-borderSvg')
        if (borderSvg) {
            borderSvg.parentNode.removeChild(borderSvg)
        }
        hoverAnimation = null
        isCreated = false
    }

    return {
        create,
        destroy,
        getHoverAnimation
    }
}